// helper functions used across the dashboard

export const legacyDashboardURL = (path) => {
  // generate URL to the legacy dashboard
  let base_redirect_path = '';
  if (import.meta.env.DEV) {
    // redirects will change ports on localhost/dev
    base_redirect_path = 'http://localhost:8000';
  }
  return base_redirect_path + path;
};

export const forceTrailingSlash = (value) => {
  // force add a slash if there is a value
  if (!value) {
    return value;
  }
  if (!value.endsWith('/')) {
    value = value + '/';
  }
  return value;
};

export const datamodelIdFromType = (datamodels, datamodelType) => {
  // find datamodel ID matching a specific type (i.e. site_custom_fields) from the datamodel list
  let match;
  datamodels.forEach((datamodel) => {
    if (datamodel.datamodel_type == datamodelType) {
      match = datamodel.id;
    }
  });
  return match;
};

export const fileAsBase64 = (acceptedFile, callback) => {
  // get the file object as base64 and pass back as a callback
  const reader = new FileReader();
  reader.addEventListener(
    'load',
    () => {
      callback({
        name: acceptedFile[0].name,
        file: reader.result.split('base64,')[1],
      });
    },
    false,
  );
  // read the first file
  reader.readAsDataURL(acceptedFile[0]);
};

export const getCookieValue = (name) =>
  // get value of cookie by name
  document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';

export const isPrimarySite = (site) => {
  // determine if the current site is the "primary site" (i.e. corporate)

  return site.location_ids.length === 0 && site.base_directory === '/';
};

export const matchDatamodelField = (field, query) => {
  // determine filter match for a particular datamodel Field when filtering

  // explicit matching only
  const exactMatchFields = [field.id.toString()];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // string match for fields
  const fieldFields = [field.help_text, field.label];
  if (fieldFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)) {
    return true;
  }

  return false;
};

// Group roles with thier user friendly names
export const GROUP_ROLES = {
  admin: 'Admins',
  no_base_perms: 'Restricted Users',
  user: 'Users',
};

export const mapGroupRoles = (roles) => {
  if (!roles?.length) return [];

  let list = [];
  for (const [key, value] of Object.entries(GROUP_ROLES)) {
    if (roles.includes(key)) {
      list.push(value);
    }
  }
  return list;
};

/*
  Get the group settings based on the group role
  @param {Object} groupData - The group data
  @returns {Object} The group settings
*/
export const getGroupSettings = (groupData = {}) => {
  let settings;
  // if role is user or no-role
  if (groupData.role === 'user' || !groupData.role) {
    settings = {};
    // if role is no_base_perms
  } else if (groupData.role === 'no_base_perms') {
    // get visibility
    const dashboardVisibility = {
      locationSocialView: !!groupData?.dashboardVisibility?.locationSocialView,
      siteBlogsView: !!groupData?.dashboardVisibility?.siteBlogsView,
      siteBlogCategoriesView:
        !!groupData?.dashboardVisibility?.siteBlogCategoriesView,
      siteBlogAuthorsView:
        !!groupData?.dashboardVisibility?.siteBlogAuthorsView,
      sitePagesView: !!groupData?.dashboardVisibility?.sitePagesView,
    };
    // only persist keys that are enabled; missing keys fall back to default visibility rules
    const cleanDashboardVisiblity = Object.fromEntries(
      Object.entries(dashboardVisibility).filter(([, v]) => !!v),
    );

    settings = {
      default_visibility: false,
      restrict_custom_fields: true,
      restrict_dashboards: ['latest'],
      dashboardVisibility: cleanDashboardVisiblity,
    };
    // if role is admin
  } else if (groupData.role === 'admin') {
    settings = {
      restrictions: {
        can_manage_themes: true,
        can_use_jinja: true,
        can_use_template: true,
      },
      restrict_dashboards: ['latest', 'legacy'],
    };
  }
  return settings;
};

/**
 * Coerce API / legacy string booleans to a real boolean for checkbox state.
 * Treats yes/no (case-insensitive) and common string/number truthy forms.
 */
export const lazyBoolean = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (['yes', 'true', '1', 'on'].includes(v)) return true;
    if (['no', 'false', '0', 'off'].includes(v)) return false;
  }
  return Boolean(value);
};
